import * as Sentry from "@sentry/react";
import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import PublicReport from "./pages/PublicReport";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";

// Every deploy renames the hashed chunks and deletes the old ones. A tab that
// was open across a deploy still holds the previous index.html, so its next
// route change requests a chunk that no longer exists — Cloudflare Pages answers
// with the SPA fallback (200 text/html), the module fails strict MIME checking,
// and the route dies. Reload once to pick up the current index.html; the guard
// stops a genuinely broken chunk from looping.
// The guard has to survive the reload it triggers, so it is a timestamp rather
// than a flag cleared on load — clearing on load lets a genuinely missing chunk
// reload forever (measured: 9,100 reloads in 20s).
const RELOAD_AT_KEY = "chunk-reload-at";
const RELOAD_WINDOW_MS = 10_000;

const reloadForFreshChunks = (): boolean => {
  const last = Number(sessionStorage.getItem(RELOAD_AT_KEY) || 0);
  if (Date.now() - last < RELOAD_WINDOW_MS) return false;
  sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
  window.location.reload();
  return true;
};

const lazyWithReload: typeof lazy = (factory) =>
  lazy(() =>
    (factory() as Promise<never>).catch((err) => {
      // Not reloading means the fresh index.html has the same broken chunk —
      // a real failure, so let the error boundary report it.
      if (!reloadForFreshChunks()) throw err;
      // Never settles: the reload replaces this document.
      return new Promise<never>(() => {});
    })
  );

window.addEventListener("vite:preloadError", (event) => {
  if (reloadForFreshChunks()) event.preventDefault();
});

const Auth = lazyWithReload(() => import("./pages/Auth"));
const Test = lazyWithReload(() => import("./pages/Test"));
const AdminLogin = lazyWithReload(() => import("./pages/AdminLogin"));
const AdminDashboard = lazyWithReload(() => import("./pages/AdminDashboard"));
const AdminGenerateLink = lazyWithReload(() => import("./pages/AdminGenerateLink"));
const AdminSessionDetail = lazyWithReload(() => import("./pages/AdminSessionDetail"));
const SuperAdmin = lazyWithReload(() => import("./pages/SuperAdmin"));
const AdminSecurityDashboard = lazyWithReload(() => import("./pages/AdminSecurityDashboard"));
const CandidateDashboard = lazyWithReload(() => import("./pages/CandidateDashboard"));
const DetailedReport = lazyWithReload(() => import("./pages/DetailedReport"));
const TestPage = lazyWithReload(() => import("./pages/TestPage"));
const DebugDatabase = lazyWithReload(() => import("./pages/DebugDatabase"));
const QuestionBankManager = lazyWithReload(() => import("./pages/QuestionBankManager"));
const ProtectedRoute = lazyWithReload(() => import("./components/ProtectedRoute"));
const StartTest = lazyWithReload(() => import("./pages/StartTest"));
const CandidateAuth = lazyWithReload(() => import("./pages/CandidateAuth"));
const MaintenanceGate = lazyWithReload(() => import("./components/MaintenanceGate"));
const ResetPassword = lazyWithReload(() => import("./pages/ResetPassword"));

// Redirect component for candidate portal
const TalentOSRedirect = () => {
  window.location.href = "https://app.weremotedevs.com/candidate";
  return null;
};

const queryClient = new QueryClient();

const SentryFallback = ({ resetError }: { error: unknown; componentStack: string; eventId: string; resetError: () => void }) => (
  <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
    <div className="max-w-md text-center">
      <h1 className="mb-4 text-2xl font-bold text-gray-900">Something went wrong</h1>
      <p className="mb-6 text-gray-600">
        An unexpected error occurred. Our team has been notified.
      </p>
      <button
        onClick={resetError}
        className="rounded-lg bg-blue-600 px-6 py-2 text-white hover:bg-blue-700"
      >
        Try Again
      </button>
    </div>
  </div>
);

const LazyFallback = () => (
  <div className="flex min-h-screen items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
  </div>
);

const App = () => (
  <Sentry.ErrorBoundary fallback={SentryFallback} showDialog>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Suspense fallback={<LazyFallback />}>
        <Routes>
          {/* Landing Page */}
          <Route path="/" element={<Index />} />
          
          {/* 🔐 SECRET Admin Login - Hidden URL */}
          <Route path="/wrd-admin-2026" element={<Auth />} />
          
          {/* Redirect old auth routes to Talent OS */}
          <Route path="/auth" element={<TalentOSRedirect />} />
          <Route path="/login" element={<TalentOSRedirect />} />
          
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/candidate-auth" element={<CandidateAuth />} />
          <Route path="/test" element={<MaintenanceGate><Test /></MaintenanceGate>} />
          <Route path="/start-test/:token" element={<MaintenanceGate><StartTest /></MaintenanceGate>} />
          
          {/* 🔍 Debug Database Tool */}
          <Route path="/debug-database" element={<DebugDatabase />} />
          <Route path="/test-page" element={<TestPage />} />
          
          {/* MAIN REPORT - Use Candidate Dashboard (Best Design) */}
          <Route path="/instant-report" element={<CandidateDashboard />} />
          <Route path="/professional-report" element={<CandidateDashboard />} />
          <Route path="/candidate-dashboard" element={<CandidateDashboard />} />
          <Route path="/candidate/dashboard" element={<CandidateDashboard />} />
          
          {/* DETAILED REPORT - Full standalone report page */}
          <Route path="/detailed-report" element={<DetailedReport />} />
          
          {/* PUBLIC REPORT - Shareable link for clients */}
          <Route path="/public-report/:token" element={<PublicReport />} />
          <Route path="/report/:token" element={<PublicReport />} />
          
          {/* LEGAL PAGES */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route 
            path="/admin/dashboard" 
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/generate-link" 
            element={
              <ProtectedRoute>
                <AdminGenerateLink />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/session/:sessionId" 
            element={
              <ProtectedRoute>
                <AdminSessionDetail />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/super" 
            element={
              <ProtectedRoute>
                <SuperAdmin />
              </ProtectedRoute>
            } 
          />
          
          {/* 🔐 NEW: Security Dashboard */}
          <Route 
            path="/admin/security" 
            element={
              <ProtectedRoute>
                <AdminSecurityDashboard />
              </ProtectedRoute>
            } 
          />
          
          {/* 📚 NEW: Question Bank Manager */}
          <Route 
            path="/admin/question-bank" 
            element={
              <ProtectedRoute>
                <QuestionBankManager />
              </ProtectedRoute>
            } 
          />
          
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
